// One emote table per channel for the whole page. Every chat connection that
// shows a channel's rows (its own chat, or a Shared Chat partner's rows in
// another channel's chat) shares the same table, the same live 7TV watch and
// the same retries, so a channel loads once however many places need it, and a
// live edit reaches every row that uses it.

import { emoteScope, startEmotes, type EmoteMap, type SevenTvPlatform } from './emoteMap';
import { watchSevenTvChannel, type EmoteSetListener } from './seventvLive';

interface Entry {
  refs: number;
  map: EmoteMap;
  ready: Promise<EmoteMap>;
  abort: AbortController;
  stopWatch: (() => void) | null;
}

const rooms = new Map<string, Entry>();

export interface RoomEmotes {
  /** Usable at once (saved lists first); filled in place as answers land. */
  map: EmoteMap;
  /** Settles once every list has answered or failed its first ask. */
  ready: Promise<EmoteMap>;
  release(): void;
}

/**
 * The channel's table, loaded on first use and dropped when its last user
 * releases it. `persist: 'spare'` (a Shared Chat partner) saves its lists only
 * in a free slot, so a passing partner never pushes out the overlay's own
 * channels but still starts warm next time; a channel later shown on its own
 * is kept in full from then on.
 * `onSetChange` hears the channel's 7TV set edits (the overlay's set-change
 * rows); only a channel's own chat asks for them.
 */
export function acquireRoomEmotes(
  platform: SevenTvPlatform,
  id: string,
  opts: { persist?: boolean | 'spare'; onSetChange?: EmoteSetListener } = {},
): RoomEmotes {
  const key = emoteScope(platform, id);
  const persist = opts.persist ?? true;
  let entry = rooms.get(key);
  if (!entry) {
    const abort = new AbortController();
    const load = startEmotes(platform, id, { signal: abort.signal, persist });
    const created: Entry = { refs: 0, map: load.map, ready: load.ready, abort, stopWatch: null };
    rooms.set(key, created);
    void load.ready.then(() => {
      if (rooms.get(key) === created && !created.stopWatch) created.stopWatch = watchSevenTvChannel(id, created.map, undefined, platform);
    });
    entry = created;
  } else if (persist === true) {
    entry.map.persist = true;
  }
  const held = entry;
  held.refs += 1;

  let released = false;
  let stopListener: (() => void) | null = null;
  if (opts.onSetChange) {
    const listener = opts.onSetChange;
    void held.ready.then(() => {
      if (!released) stopListener = watchSevenTvChannel(id, held.map, listener, platform);
    });
  }

  return {
    map: held.map,
    ready: held.ready,
    release: () => {
      if (released) return;
      released = true;
      stopListener?.();
      held.refs -= 1;
      if (held.refs > 0 || rooms.get(key) !== held) return;
      rooms.delete(key);
      held.abort.abort();
      held.stopWatch?.();
    },
  };
}
